import { ref, onScopeDispose, computed, readonly } from 'vue'
import { newWebSocketRpcSession } from 'capnweb'
import type { AppFeatureFlagsRpc } from '@rialtic/types'
import { useNuxtApp, useRuntimeConfig } from '#app'
import { useAuthAdapter } from './useAuthAdapter'
import type { RumGlobal } from '@datadog/browser-rum'

export interface FeatureFlagsConnectionState {
  isConnected: boolean
  isConnecting: boolean
  error: string | null
  retryCount: number
}

class AuthenticationUnavailableError extends Error {
  constructor() {
    super('Authentication unavailable')
    this.name = 'AuthenticationUnavailableError'
  }
}

class TokenUnavailableError extends Error {
  constructor() {
    super('No JWT token available for RPC authentication')
    this.name = 'TokenUnavailableError'
  }
}

// Singleton state and connections
const createSharedState = () =>
  ref<FeatureFlagsConnectionState>({
    isConnected: false,
    isConnecting: false,
    error: null,
    retryCount: 0,
  })

let sharedState: ReturnType<typeof createSharedState> | undefined
let rpcSession: AppFeatureFlagsRpc | null = null
let websocket: WebSocket | null = null
let reconnectTimeoutId: ReturnType<typeof setTimeout> | null = null

/**
 * Feature Flags WebSocket RPC Connection Management
 *
 * Handles the Cap'n Web WebSocket RPC connection lifecycle
 * for feature flags including authentication, reconnection, and error handling.
 *
 * This is a singleton - all calls share the same connection.
 */
export const useFeatureFlagsConnection = () => {
  // Initialize shared state only once
  if (!sharedState) {
    sharedState = createSharedState()
  }

  const state = sharedState

  const config = useRuntimeConfig()
  const { workersApiUrl, retryAttempts, reconnectDelay } =
    config.public.rialticShared

  const { $datadog } = useNuxtApp() as unknown as {
    $datadog: RumGlobal
  }

  /**
   * Get JWT token using auth adapter
   */
  const getAuthToken = async (): Promise<string> => {
    try {
      const authAdapter = useAuthAdapter()
      const isAuthenticated = await authAdapter.isAuthenticated()
      if (!isAuthenticated) {
        throw new AuthenticationUnavailableError()
      }

      const token = await authAdapter.getAccessToken()

      if (!token) {
        console.warn('No JWT token available for RPC authentication')
        $datadog.addAction('feature-flags-auth-token-unavailable', {
          url: workersApiUrl,
        })
        throw new TokenUnavailableError()
      }

      return token
    } catch (error) {
      if (
        error instanceof AuthenticationUnavailableError ||
        error instanceof TokenUnavailableError
      ) {
        throw error
      }

      $datadog.addError(error, {
        function: 'useFeatureFlagsConnection:getAuthToken',
      })
      throw error
    }
  }

  /**
   * Create WebSocket connection with authentication
   */
  const createWebSocket = async (): Promise<WebSocket> => {
    try {
      const token = await getAuthToken()
      const wsUrl = `${workersApiUrl}/rpc/feature-flags`.replace('http', 'ws')
      const wsUrlWithAuth = `${wsUrl}?token=${encodeURIComponent(token)}`

      const ws = new WebSocket(wsUrlWithAuth)

      return new Promise((resolve, reject) => {
        ws.onopen = () => {
          $datadog.addAction('feature-flags-websocket-connected', {
            url: wsUrl,
          })
          resolve(ws)
        }

        ws.onerror = () => {
          $datadog.addError(new Error('WebSocket connection failed'), {
            function: 'useFeatureFlagsConnection:createWebSocket',
            url: wsUrl,
          })
          reject(new Error('WebSocket connection failed'))
        }

        ws.onclose = (event) => {
          $datadog.addAction('feature-flags-websocket-closed', {
            code: event.code,
            reason: event.reason,
          })
          handleDisconnection()
        }
      })
    } catch (error) {
      if (
        error instanceof AuthenticationUnavailableError ||
        error instanceof TokenUnavailableError
      ) {
        throw error
      }

      $datadog.addError(error, {
        function: 'useFeatureFlagsConnection:createWebSocket',
      })
      throw error
    }
  }

  /**
   * Handle WebSocket disconnection and attempt reconnection
   */
  const handleDisconnection = () => {
    state.value.isConnected = false
    rpcSession = null
    websocket = null

    if (state.value.retryCount < retryAttempts) {
      state.value.retryCount++

      $datadog.addAction('feature-flags-reconnection-attempt', {
        attempt: state.value.retryCount,
        maxAttempts: retryAttempts,
      })

      reconnectTimeoutId = setTimeout(() => {
        connect()
      }, reconnectDelay)
    } else {
      state.value.error = 'Max reconnection attempts exceeded'
      $datadog.addError(new Error('Max RPC reconnection attempts exceeded'), {
        function: 'useFeatureFlagsConnection:handleDisconnection',
        retryCount: state.value.retryCount,
        maxRetries: retryAttempts,
      })
    }
  }

  /**
   * Connect to the RPC endpoint
   */
  const connect = async (): Promise<AppFeatureFlagsRpc | null> => {
    if (state.value.isConnecting || state.value.isConnected) {
      return rpcSession
    }

    state.value.isConnecting = true
    state.value.error = null

    try {
      // Create WebSocket connection
      websocket = await createWebSocket()

      // Create RPC session
      rpcSession = newWebSocketRpcSession<AppFeatureFlagsRpc>(websocket)

      state.value.isConnected = true
      state.value.isConnecting = false
      state.value.retryCount = 0

      $datadog.addAction('feature-flags-rpc-session-established', {
        url: workersApiUrl,
      })

      return rpcSession
    } catch (error) {
      if (error instanceof AuthenticationUnavailableError) {
        state.value.isConnecting = false
        state.value.error = null
        return null
      }

      if (error instanceof TokenUnavailableError) {
        state.value.isConnecting = false
        state.value.error = null
        handleDisconnection()
        return null
      }

      state.value.isConnecting = false
      state.value.error =
        error instanceof Error ? error.message : 'Connection failed'

      $datadog.addError(error, {
        function: 'useFeatureFlagsConnection:connect',
      })

      // Trigger reconnection logic
      handleDisconnection()
      return null
    }
  }

  /**
   * Disconnect from RPC endpoint
   */
  const disconnect = () => {
    if (reconnectTimeoutId) {
      clearTimeout(reconnectTimeoutId)
      reconnectTimeoutId = null
    }

    if (websocket) {
      websocket.close()
      websocket = null
    }

    rpcSession = null
    state.value.isConnected = false
    state.value.isConnecting = false
    state.value.retryCount = 0

    $datadog.addAction('feature-flags-rpc-disconnected', {})
  }

  /**
   * Get the current RPC session (connect if needed)
   */
  const getSession = async (): Promise<AppFeatureFlagsRpc | null> => {
    if (rpcSession && state.value.isConnected) {
      return rpcSession
    }

    return await connect()
  }

  onScopeDispose(() => {
    disconnect()
  })

  return {
    state: readonly(state),
    isConnected: computed(() => state.value.isConnected),
    isConnecting: computed(() => state.value.isConnecting),
    error: computed(() => state.value.error),
    connect,
    disconnect,
    getSession,
  }
}
